import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Warning } from "@phosphor-icons/react";
import api from "../lib/api";
import { zar, fmtDate, fmtDateTime } from "../lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import PeriodSelector, { rangeFor } from "../components/PeriodSelector";
import { DailyBrief } from "../components/Assistant";
import { toast } from "sonner";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Dashboard() {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(rangeFor("month"));
  const [d, setD] = useState(null);

  const load = () =>
    api.get(`/dashboard/summary?start=${range[0]}&end=${range[1]}`)
      .then((r) => setD(r.data))
      .catch(() => toast.error("Failed to load dashboard"));
  useEffect(() => { load(); }, [range]);

  const onPreset = (p) => {
    setPreset(p);
    if (p !== "custom") setRange(rangeFor(p));
  };

  const decide = async (id, status) => {
    try {
      await api.post(`/leave/${id}/decide`, { status, paid: true });
      toast.success(`Leave ${status}`);
      load();
    } catch {
      toast.error("Action failed");
    }
  };

  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  const { revenue, expenses, labour, notifications, covers } = d;

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Every module at a glance for the selected period</p>
        </div>
        <PeriodSelector preset={preset} start={range[0]} end={range[1]} onPreset={onPreset} onRange={setRange} testid="dash-period" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-border shadow-none" data-testid="dash-revenue">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Revenue</CardTitle>
            <Link to="/finance?tab=revenue"><Button variant="outline" size="sm" data-testid="dash-revenue-link">Open Finance</Button></Link>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">Sales</span>
              <span className="font-heading text-2xl font-bold num" data-testid="dash-sales">{zar(revenue.sales)}</span>
            </div>
            <div className="text-xs text-muted-foreground flex gap-4 num">
              <span>Food {zar(revenue.food)}</span><span>Bev {zar(revenue.beverage)}</span>
              {revenue.additional > 0 && <span>Other {zar(revenue.additional)}</span>}
            </div>
            <div className="flex items-baseline justify-between border-t pt-3">
              <span className="text-sm text-muted-foreground">Tips</span>
              <span className="font-heading text-xl font-semibold num" data-testid="dash-tips">{revenue.tips != null ? zar(revenue.tips) : "—"}</span>
            </div>
            {(revenue.functions || 0) > 0 && (
              <div className="flex items-baseline justify-between border-t pt-3" data-testid="dash-revenue-functions">
                <span className="text-sm text-muted-foreground">Functions (recognized on event date)</span>
                <span className="font-heading text-xl font-semibold num text-[#C9A8FF]">{zar(revenue.functions)}</span>
              </div>
            )}
            <div className="flex items-baseline justify-between border-t pt-3">
              <span className="text-sm font-medium">Total incl. tips{(revenue.functions || 0) > 0 ? " & functions" : ""}</span>
              <span className="font-heading text-xl font-bold num text-[#A9C97E]" data-testid="dash-revenue-total">{zar((revenue.tips != null ? revenue.total : revenue.sales) + (revenue.functions || 0))}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-none" data-testid="dash-expenses">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Expenses</CardTitle>
            <Link to="/finance?tab=expenses"><Button variant="outline" size="sm" data-testid="dash-expenses-link">Open Expenses</Button></Link>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">Invoices (period)</span>
              <span className="font-heading text-2xl font-bold num" data-testid="dash-invoice-spend">{zar(expenses.net_invoices)}</span>
            </div>
            {expenses.credit_notes > 0 && (
              <p className="text-xs text-muted-foreground num">incl. {zar(expenses.invoices)} less {zar(expenses.credit_notes)} credit notes</p>
            )}
            <div className="flex items-baseline justify-between border-t pt-3">
              <span className="text-sm text-muted-foreground">Fixed expenses (monthly run-rate)</span>
              <span className="font-heading text-lg font-semibold num" data-testid="dash-fixed">{zar(expenses.fixed_monthly)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-none" data-testid="dash-labour">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Labour</CardTitle>
            <Link to="/hours"><Button variant="outline" size="sm" data-testid="dash-hours-link">Open Hours</Button></Link>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">Hours worked</span>
              <span className="font-heading text-2xl font-bold num" data-testid="dash-hours">{labour.hours}h</span>
            </div>
            {labour.over_cap.length > 0 ? (
              <div className="border-t pt-3 space-y-1.5" data-testid="dash-over-cap">
                <p className="text-xs font-medium text-[#F87171] flex items-center gap-1">
                  <Warning size={13} weight="fill" /> Over {labour.cap}h weekly cap
                </p>
                {labour.over_cap.slice(0, 6).map((o) => (
                  <p key={`${o.employee_id}-${o.week_start}`} className="text-xs text-muted-foreground num" data-testid={`dash-overcap-${o.employee_id}-${o.week_start}`}>
                    {o.employee_name} · wk {fmtDate(o.week_start)} · {o.hours}h
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground border-t pt-3" data-testid="dash-over-cap-none">Nobody over the {labour.cap}h weekly cap in this period.</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-border shadow-none" data-testid="dash-notifications">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Notifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm num" data-testid="dash-notif-counts">
              <span data-testid="dash-pending-leave-count">{notifications.pending_leave_count} leave request(s)</span>
              <span data-testid="dash-invoice-reviews">{notifications.pending_invoice_reviews} invoice review(s)</span>
              <span data-testid="dash-flagged">{notifications.flagged_entries} flagged clock entry(s)</span>
            </div>
            {notifications.pending_leave.length > 0 && (
              <div className="space-y-3 border-t pt-3" data-testid="dashboard-pending-leave">
                {notifications.pending_leave.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-3" data-testid={`pending-leave-${l.id}`}>
                    <div>
                      <p className="text-sm font-medium">{l.employee_name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{l.type} · {fmtDate(l.start_date)} → {fmtDate(l.end_date)} ({l.days}d)</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" data-testid={`approve-leave-${l.id}`} onClick={() => decide(l.id, "approved")}
                        className="bg-primary hover:bg-[#8B5CF6] text-white">Approve</Button>
                      <Button size="sm" variant="outline" data-testid={`decline-leave-${l.id}`} onClick={() => decide(l.id, "declined")}>Decline</Button>
                    </div>
                  </div>
                ))}
                <Link to="/leave" className="block"><Button variant="outline" size="sm" data-testid="view-all-leave-btn">View all leave</Button></Link>
              </div>
            )}
            {notifications.recent?.length > 0 && (
              <div className="space-y-2 border-t pt-3" data-testid="dash-notif-recent">
                {notifications.recent.map((n) => (
                  <div key={n.id}>
                    <p className="text-xs font-medium">{n.title}</p>
                    <p className="text-[10px] text-muted-foreground">{fmtDateTime(n.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-border shadow-none" data-testid="dash-covers">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg">Average spend per head</CardTitle>
          <Link to="/finance?tab=revenue&rsub=input"><Button variant="outline" size="sm" data-testid="dash-covers-link">Capture covers</Button></Link>
        </CardHeader>
        <CardContent>
          {!covers || covers.days.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="dash-covers-empty">
              No covers captured in this period. Add optional lunch/dinner covers on the Revenue input to see average spend per head.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">7-day rolling avg / head</p>
                  <p className="font-heading text-2xl font-bold num text-[#C9A8FF]" data-testid="dash-covers-rolling">{zar(covers.rolling7.total)}</p>
                  <p className="text-[10px] text-muted-foreground" data-testid="dash-covers-rolling-days">{covers.rolling7.days} day(s) with covers in window</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Rolling food / head</p>
                  <p className="font-heading text-lg font-semibold num" data-testid="dash-covers-rolling-food">{zar(covers.rolling7.food)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Rolling beverage / head</p>
                  <p className="font-heading text-lg font-semibold num" data-testid="dash-covers-rolling-bev">{zar(covers.rolling7.beverage)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Covers (period)</p>
                  <p className="font-heading text-lg font-semibold num" data-testid="dash-covers-total">{covers.total_covers}</p>
                </div>
              </div>
              <div className="h-56" data-testid="dash-covers-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={covers.days.map((x) => ({ date: x.date.slice(5), spend: x.total_spend, rolling: x.rolling7.total, covers: x.covers }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2A2440" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8E87A3" }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#8E87A3" }} tickLine={false} axisLine={false} tickFormatter={(v) => `R${v}`} width={64} />
                    <Tooltip contentStyle={{ background: "#151226", border: "1px solid #2A2440", borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: "#BDB8CC" }}
                      formatter={(v, name) => [zar(v), name === "spend" ? "Daily avg / head" : "7-day rolling"]} />
                    <Bar dataKey="spend" fill="#9D5CFF" radius={[4, 4, 0, 0]} maxBarSize={28} />
                    <Line type="monotone" dataKey="rolling" stroke="#FF5C00" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[11px] text-muted-foreground">Days without covers are excluded. Tips excluded; daily average = (food + beverage + additional) ÷ total covers.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <DailyBrief />
    </div>
  );
}
